/*
 * Holds Add to cart shut until the shopper has chosen every option.
 *
 * Why this can't live in Liquid: Shopify resolves a *partial* option selection to a whole
 * variant server-side. Request the FLY40 with only Hand=Left and the section comes back as
 * "Left / 77 / P92", with all three option groups rendered `checked` and the submit button
 * enabled. So the moment the shopper picks one option, the server hands back a page that looks
 * like they picked all three. `snippets/product-variant-options.liquid` suppresses the initial
 * pre-selection; this file is what keeps it suppressed afterwards.
 *
 * The gate is deliberately JS-only. The Liquid renders the button enabled, so a shopper with
 * no JS is no worse off than before this existed (they submit the default variant, as they
 * always did) rather than being locked out of multi-variant products entirely.
 *
 * Groups are keyed on the input `name` / select `name`, which `product-variant-options.liquid`
 * builds from the option name and position -- stable across the section re-renders that
 * replace these nodes wholesale.
 *
 * The gate only engages where Liquid rendered `data-options-gate` on `<variant-selects>`. A
 * `?variant=` deep link (Merchant Center, Google Shopping) comes back ungated, with every
 * option selected; there is nothing to hold shut, and clearing it would strip the variant the
 * ad paid for.
 */
(() => {
  const GATE_ATTR = 'data-options-gated';

  class ProductOptionsGate {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.picked = new Set();
      this.reasserting = false;

      this.onChange = this.onChange.bind(this);
      this.enforce = this.enforce.bind(this);

      // Delegated: the variant-selects subtree is replaced on every option change, so a
      // listener bound to the inputs themselves would not survive the first pick.
      this.productInfo.addEventListener('change', this.onChange);

      // Strip the server's re-selection out of the incoming markup *before* it is swapped in,
      // so the untouched groups never flash as selected.
      this.productInfo.addPreProcessCallback?.((html) => this.clearUnpicked(html));

      // The submit button is re-enabled from the server response after every change
      // (product-info.js `handleUpdateProductInfo`), and the no-matching-variant path sets it
      // to "Unavailable" without publishing variantChange. Rather than race either, watch the
      // button and re-assert whenever something disagrees with the gate.
      this.observer = new MutationObserver(this.enforce);

      this.enforce();
      this.observe();
    }

    get productForm() {
      return this.productInfo.querySelector('product-form');
    }

    get submitButton() {
      return this.productInfo.querySelector('.product-form__submit');
    }

    /* Option groups present in `root`, as a map of name -> controls. */
    groups(root = this.productInfo) {
      const groups = new Map();
      const add = (control, value) => {
        if (!groups.has(control.name)) groups.set(control.name, []);
        const controls = groups.get(control.name);
        // `HTMLUpdateUtility.viewTransition` keeps the outgoing `variant-selects`
        // in the DOM, hidden, for 500ms after inserting its replacement. Counting both copies
        // makes a one-value group (Flex) look like a two-value one, so `clearUnpicked` would
        // clear it -- precisely the bug this file exists to prevent. Keep the first control for
        // each value; the new node is inserted before the old one, so that is the live one.
        if (controls.some((existing) => existing.value === value && existing !== control)) return;
        controls.push(control);
      };
      root.querySelectorAll('variant-selects input[type="radio"]').forEach((input) => add(input, input.value));
      root.querySelectorAll('variant-selects select').forEach((select) => {
        groups.set(select.name, [select]);
      });
      return groups;
    }

    /*
     * A group with only one possible value is not a choice -- the Liquid leaves it pre-selected
     * (see `product-variant-options.liquid`), so it must count as satisfied here or the gate
     * could never open on, say, a stick with three flexes but one blade pattern.
     */
    static isSingleValue(controls) {
      const [first] = controls;
      if (first?.tagName === 'SELECT') {
        return [...first.options].filter((option) => option.value !== '').length <= 1;
      }
      return controls.length <= 1;
    }

    static hasSelection(controls) {
      return controls.some((control) => (control.tagName === 'SELECT' ? control.value !== '' : control.checked));
    }

    get complete() {
      const groups = this.groups();
      if (!groups.size) return false;
      return [...groups.entries()].every(([name, controls]) => {
        // A single-value group is not a choice: it is always satisfied, whether or not its
        // control still carries `checked`. `clearUnpicked` below re-asserts that state so the
        // submitted form keeps the value, but completion must not hinge on it -- a stale
        // re-render must never strand the shopper on a disabled "Choose options".
        if (ProductOptionsGate.isSingleValue(controls)) return true;
        return this.picked.has(name) && ProductOptionsGate.hasSelection(controls);
      });
    }

    onChange(event) {
      const control = event.target;
      if (!control.name || !control.closest('variant-selects')) return;
      this.picked.add(control.name);
      // Nothing else to do here: product-info re-renders in response to the same event, and
      // the preprocess callback plus the observer handle the result.
    }

    /* Uncheck every group the shopper has not explicitly chosen. */
    clearUnpicked(root) {
      this.groups(root).forEach((controls, name) => {
        if (this.picked.has(name)) return;
        if (ProductOptionsGate.isSingleValue(controls)) {
          // Re-assert rather than merely skip. A re-render can leave the single value
          // unchecked (see `groups`), and a cleared value both reads as an unfinished choice
          // and drops out of the submitted form.
          controls.forEach((control) => {
            if (control.tagName === 'SELECT') {
              const [onlyValue] = [...control.options].filter((option) => option.value !== '');
              if (onlyValue) onlyValue.selected = true;
            } else {
              control.checked = true;
              control.setAttribute('checked', '');
            }
          });
          return;
        }
        controls.forEach((control) => {
          if (control.tagName === 'SELECT') {
            const placeholder = control.querySelector('option[value=""]');
            if (placeholder) placeholder.selected = true;
          } else {
            control.checked = false;
            control.removeAttribute('checked');
          }
        });
      });
    }

    enforce() {
      if (this.reasserting) return;
      const button = this.submitButton;
      if (!button) return;

      if (this.complete) {
        this.productInfo.removeAttribute(GATE_ATTR);
        this.observer.disconnect();
        return;
      }

      this.clearUnpicked(this.productInfo);
      this.productInfo.setAttribute(GATE_ATTR, 'true');
      this.stripVariantParam();

      const label = button.querySelector('span');
      const wanted = window.variantStrings?.chooseOptions || 'Choose options';
      const alreadyGated = button.hasAttribute('disabled') && label?.textContent.trim() === wanted;
      if (alreadyGated) return;

      // Guard the observer against the mutations we are about to cause ourselves.
      this.reasserting = true;
      this.productForm?.toggleSubmitButton(true, wanted);
      this.reasserting = false;
    }

    /*
     * product-info writes `?variant=` from the server's fill-in after every option change.
     * While the gate is closed that URL names a variant the shopper has not chosen -- and
     * because `gate_options` keys off `product.selected_variant`, reloading such a URL would
     * come back with every option pre-selected and the gate silently undone. Keep the
     * parameter out until the choice is actually complete.
     */
    stripVariantParam() {
      if (this.productInfo.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      if (!url.searchParams.has('variant')) return;
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    observe() {
      const button = this.submitButton;
      if (!button) return;
      this.observer.observe(button, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  const gate = (productInfo) => {
    if (productInfo.optionsGate) return;
    // Nothing to gate on a single-variant product.
    if (!productInfo.querySelector('variant-selects input, variant-selects select')) return;
    // Only the renders Liquid marked as gated. `?variant=` deep links and products whose only
    // available variant is already chosen come back ungated, button live; re-gating them would
    // disable a page that has nothing left to choose.
    if (!productInfo.querySelector('variant-selects[data-options-gate="true"]')) return;
    productInfo.optionsGate = new ProductOptionsGate(productInfo);
  };

  document.addEventListener('product-info:loaded', (event) => gate(event.target));
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('product-info').forEach(gate);
  });
  if (document.readyState !== 'loading') {
    document.querySelectorAll('product-info').forEach(gate);
  }
})();
